import { Mail, Eye, Plus, Trash2, Save, Check } from "lucide-react";
import {
  Box,
  Heading,
  Button,
  Flex,
  Link,
  Stack,
  Text,
  Input,
  NativeSelect,
  Field,
  Spinner,
  Card,
  Separator,
  SimpleGrid,
  Steps,
  Table,
  Badge,
  Dialog,
  Alert,
  ButtonGroup,
} from "@chakra-ui/react";
import { useSession } from "../../../hooks/useSession";

import {
  InvoiceDeliveryType,
  InvoiceStatus,
  OpenAPI,
} from "../../../generated";
import { useParams } from "react-router-dom";
import { NotFound } from "../../../404";
import {
  useInvoiceDelete,
  useInvoiceDetail,
} from "../../../hooks/useInvoiceDetail";
import { useDuplicateInvoice } from "../../../hooks/useDuplicateInvoice";
import { getColorScheme } from "../../../utils/colorScheme";
import { getFormattedDate } from "../../../utils/dates";
import { useInvoicesQueue } from "../../../hooks/useInvoicesQueue";
import { useInvoiceRegister } from "../../../hooks/useInvoiceRegister";
import { useInvoiceLinesUpdate } from "../../../hooks/useInvoiceLinesUpdate";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { InvoiceStatusButton } from "./InvoiceStatusButton";
import { Camelize } from "camelize-ts";
import type { InvoiceDetailSchema } from "../../../generated";

const deliverySteps = [
  {
    status: "PENDING",
    title: "Venter",
    description: "Venter på utsending",
  },
  { status: "QUEUED", title: "Lagt i kø", description: "I kø for å sendes ut" },
  { status: "SENT", title: "Utsendt", description: "Utsendt" },
];

type InvoiceLineFormValues = {
  invoiceLines: {
    id: number | null;
    invoicedQuantity: number;
    description: string;
    netUnitAmount: number;
    vatRatePercentage: number;
  }[];
};

function EditableInvoiceLines({
  invoice,
}: {
  invoice: Camelize<InvoiceDetailSchema>;
}) {
  const linesUpdateMutation = useInvoiceLinesUpdate();

  const { register, control, getValues, handleSubmit, reset } =
    useForm<InvoiceLineFormValues>({
      defaultValues: {
        invoiceLines: invoice.lines.map((line) => ({
          id: line.id,
          invoicedQuantity: line.invoicedQuantity,
          description: line.description,
          netUnitAmount: Number(line.netUnitAmount),
          vatRatePercentage: line.vatRatePercentage,
        })),
      },
    });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "invoiceLines",
  });

  const watchedLines = getValues("invoiceLines");

  useEffect(() => {
    reset({
      invoiceLines: invoice.lines.map((line) => ({
        id: line.id,
        invoicedQuantity: line.invoicedQuantity,
        description: line.description,
        netUnitAmount: Number(line.netUnitAmount),
        vatRatePercentage: line.vatRatePercentage,
      })),
    });
  }, [invoice.lines, reset]);

  const onSubmit = (data: InvoiceLineFormValues) => {
    linesUpdateMutation.mutate({
      invoiceUuid: invoice.uuid,
      requestBody: {
        invoice_lines: data.invoiceLines.map((line) => ({
          id: line.id,
          invoiced_quantity: line.invoicedQuantity,
          description: line.description,
          net_unit_amount: line.netUnitAmount,
          vat_rate_percentage: line.vatRatePercentage,
        })),
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={4}>
        {fields.map((field, i) => {
          const invoiceLine = watchedLines?.[i];
          return (
            <Stack
              key={field.id}
              direction="row"
              wrap={["wrap", "wrap", "inherit"]}
            >
              <Field.Root minWidth={100} maxWidth={100}>
                <Field.Label>Antall</Field.Label>
                <Input
                  type="number"
                  {...register(`invoiceLines.${i}.invoicedQuantity`, {
                    valueAsNumber: true,
                  })}
                  placeholder="Antall"
                  min={1}
                  onChange={(e) => {
                    update(i, {
                      ...invoiceLine,
                      invoicedQuantity: parseInt(e.target.value, 10),
                    });
                  }}
                />
              </Field.Root>
              <Field.Root minWidth={300} maxWidth={450}>
                <Field.Label>Beskrivelse</Field.Label>
                <Input
                  {...register(`invoiceLines.${i}.description`)}
                  placeholder="Beskrivelse"
                />
              </Field.Root>
              <Field.Root minWidth={150} maxWidth={150}>
                <Field.Label>Enhetspris</Field.Label>
                <Input
                  type="number"
                  {...register(`invoiceLines.${i}.netUnitAmount`, {
                    valueAsNumber: true,
                  })}
                  placeholder="100"
                />
              </Field.Root>
              <Field.Root minWidth={75} maxWidth={100}>
                <Field.Label>MVA</Field.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    {...register(`invoiceLines.${i}.vatRatePercentage`, {
                      valueAsNumber: true,
                    })}
                  >
                    <option value={0}>0%</option>
                    <option value={15}>15%</option>
                    <option value={25}>25%</option>
                  </NativeSelect.Field>
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root minWidth={100} maxWidth={100}>
                <Field.Label>Sum</Field.Label>
                <Input
                  disabled={true}
                  value={(
                    (invoiceLine?.invoicedQuantity || 0) *
                    (invoiceLine?.netUnitAmount || 0) *
                    (1 + (invoiceLine?.vatRatePercentage || 0) / 100)
                  ).toFixed(2)}
                />
              </Field.Root>
              <Flex alignItems="flex-end">
                <Button onClick={() => remove(i)}>
                  <Trash2 />
                </Button>
              </Flex>
            </Stack>
          );
        })}
        <Stack direction="row" gap={2}>
          <Button
            onClick={() =>
              append({
                id: null,
                invoicedQuantity: 1,
                description: "",
                netUnitAmount: 0,
                vatRatePercentage: 25,
              })
            }
            variant="outline"
          >
            <Plus /> Legg til linje
          </Button>
          <Button
            type="submit"
            colorPalette="blue"
            loading={linesUpdateMutation.isPending}
          >
            <Save /> Lagre linjer
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}

function ReadOnlyInvoiceLines({
  invoice,
}: {
  invoice: Camelize<InvoiceDetailSchema>;
}) {
  return (
    <Table.ScrollArea>
      <Table.Root size="md">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Antall</Table.ColumnHeader>
            <Table.ColumnHeader>Beskrivelse</Table.ColumnHeader>
            <Table.ColumnHeader>Beløp</Table.ColumnHeader>
            <Table.ColumnHeader>Sum</Table.ColumnHeader>
            <Table.ColumnHeader>MVA</Table.ColumnHeader>
            <Table.ColumnHeader>Total</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {invoice.lines.map((line) => (
            <Table.Row key={line.uuid}>
              <Table.Cell>{line.invoicedQuantity}</Table.Cell>
              <Table.Cell>{line.description}</Table.Cell>
              <Table.Cell>{line.netUnitAmount}</Table.Cell>
              <Table.Cell>{line.netAmount}</Table.Cell>
              <Table.Cell>{line.vatRatePercentage}%</Table.Cell>
              <Table.Cell>{line.grossAmount}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  );
}

export default function InvoiceDetailPage() {
  const { data: session } = useSession();

  const { id } = useParams();
  const { data: invoice, isLoading } = useInvoiceDetail(id || "");
  const [open, setOpen] = useState(false);
  const cancelRef = useRef(null);

  const invoiceDeleteMutation = useInvoiceDelete();
  const invoicesQueueMutation = useInvoicesQueue();
  const invoiceRegisterMutation = useInvoiceRegister();
  const duplicateInvoiceMutation = useDuplicateInvoice();

  if (isLoading) {
    return <Spinner />;
  }

  if (!invoice) {
    return <NotFound />;
  }

  const isDraft = invoice.status === InvoiceStatus.DRAFT;
  const isRegistered = invoice.status === InvoiceStatus.REGISTERED;

  return (
    <Stack>
      <Heading as="h1" mb={4}>
        Faktura {invoice.invoiceNumber}
      </Heading>
      {isDraft && (
        <Alert.Root status="info">
          <Alert.Indicator />
          Denne fakturaen er lagret som et utkast. Klikk &quot;Registrer
          faktura&quot; for å generere fakturanummer.
        </Alert.Root>
      )}
      <Stack direction="row" flexWrap="wrap">
        <ButtonGroup>
          {isDraft && (
            <Button
              onClick={() => invoiceRegisterMutation.mutate(invoice.uuid)}
              colorPalette="blue"
              loading={invoiceRegisterMutation.isPending}
            >
              <Check /> Registrer faktura
            </Button>
          )}
          {isRegistered &&
            invoice.deliveryType !== InvoiceDeliveryType.NONE && (
              <Button
                onClick={() => invoicesQueueMutation.mutate(invoice.uuid)}
                colorPalette="blue"
                loading={invoicesQueueMutation.isPending}
              >
                <Mail /> Send faktura
              </Button>
            )}
          <Link
            href={`${OpenAPI.BASE}/invoices/${invoice.uuid}/pdf/`}
            target="_blank"
          >
            <Button>
              {isDraft ? "Forhåndsvis PDF" : "Vis PDF"}
              <Eye />
            </Button>
          </Link>
          <Button
            onClick={() => duplicateInvoiceMutation.mutate(invoice.uuid)}
            loading={duplicateInvoiceMutation.isPending}
          >
            Dupliser
          </Button>
          <InvoiceStatusButton invoice={invoice} />
          {session?.email?.endsWith("@faktur.no") && (
            <Link
              href={`${OpenAPI.BASE}/admin/invoices/invoice/${invoice.id}/change/`}
              target="_blank"
            >
              <Button variant="outline">Admin</Button>
            </Link>
          )}
          {isDraft && (
            <Button colorPalette="red" onClick={() => setOpen(true)}>
              Slett
            </Button>
          )}
        </ButtonGroup>
        <Dialog.Root
          open={open}
          onOpenChange={(e) => setOpen(e.open)}
          role="alertdialog"
        >
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header fontSize="lg" fontWeight="bold">
                Slette faktura
              </Dialog.Header>
              <Dialog.Footer>
                {invoiceDeleteMutation.isPending && <Spinner />}
                {!invoiceDeleteMutation.isPending && (
                  <>
                    <Button ref={cancelRef} onClick={() => setOpen(false)}>
                      Avbryt
                    </Button>
                    <Button
                      colorPalette="red"
                      onClick={() => {
                        invoiceDeleteMutation.mutate({
                          invoiceUuid: invoice.uuid,
                        });
                      }}
                      ml={3}
                    >
                      Slett
                    </Button>
                  </>
                )}
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      </Stack>
      <Separator />
      {isLoading && (
        <Flex flex={1} justifyContent="center">
          <Spinner />
        </Flex>
      )}
      {!isLoading && (
        <>
          <SimpleGrid gap={4} columns={{ base: 1, md: 2 }}>
            <Card.Root>
              <Card.Header>
                <Heading size="md">Oppsummering</Heading>
              </Card.Header>

              <Card.Body pt={0}>
                <Stack separator={<Separator />} gap="4">
                  <Table.ScrollArea>
                    <Table.Root size="sm">
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell>Kundens navn</Table.Cell>
                          <Table.Cell>{invoice.customerName}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Organisasjonsnummer</Table.Cell>
                          <Table.Cell>
                            {invoice.customerIdentifier}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>KID / Melding</Table.Cell>
                          <Table.Cell>
                            {invoice.customerIdentificationNumber}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Status:</Table.Cell>
                          <Table.Cell>
                            <Badge
                              colorPalette={getColorScheme(invoice.status)}
                              fontSize={12}
                              p={1}
                              borderRadius={4}
                            >
                              {invoice.status}
                            </Badge>
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Utsendingsmetode:</Table.Cell>
                          <Table.Cell>
                            <Badge fontSize={12} p={1} borderRadius={4}>
                              {invoice.deliveryType}
                            </Badge>
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Betalt dato:</Table.Cell>
                          <Table.Cell>
                            {invoice.paidDate
                              ? getFormattedDate(invoice.paidDate)
                              : "-"}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Fakturadato:</Table.Cell>
                          <Table.Cell>{invoice.issueDate}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Forfallsdato:</Table.Cell>
                          <Table.Cell>{invoice.dueDate}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Kontonummer:</Table.Cell>
                          <Table.Cell>
                            {invoice.bankAccountNumber}
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table.Root>
                  </Table.ScrollArea>
                </Stack>
              </Card.Body>
            </Card.Root>
            <Card.Root>
              <Card.Header>
                <Heading size="md">Utsendinger</Heading>
              </Card.Header>
              <Card.Body>
                {invoice.deliveries.length === 0 && (
                  <>
                    {invoice.deliveryType === InvoiceDeliveryType.NONE && (
                      <Text>
                        Fakturaen har ingen valgt utsendingsmetode.
                      </Text>
                    )}
                    {invoice.deliveryType !== InvoiceDeliveryType.NONE && (
                      <>
                        <Text>Ingen utsending enda.</Text>
                        <Text>
                          Fakturaen blir utsendt som:{" "}
                          {invoice.deliveryType}
                        </Text>
                      </>
                    )}

                    {invoice.deliveryType ===
                      InvoiceDeliveryType.EMAIL && (
                      <Steps.Root
                        defaultStep={0}
                        count={deliverySteps.length}
                        mt={4}
                        mb={4}
                      >
                        <Steps.List>
                          {deliverySteps.map((step, index) => (
                            <Steps.Item key={index} index={index}>
                              <Steps.Indicator />
                              <Steps.Title>{step.title}</Steps.Title>
                              <Steps.Separator />
                            </Steps.Item>
                          ))}
                        </Steps.List>
                      </Steps.Root>
                    )}
                  </>
                )}
                {invoice.deliveries.map((delivery) => (
                  <Box key={delivery.uuid}>
                    <Table.ScrollArea>
                      <Table.Root size="sm">
                        <Table.Body>
                          <Table.Row>
                            <Table.Cell>ID</Table.Cell>
                            <Table.Cell>
                              {delivery.uuid.slice(0, 8)}
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Form</Table.Cell>
                            <Table.Cell>
                              {delivery.deliveryType}
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>Sendt tidspunkt</Table.Cell>
                            <Table.Cell>
                              {delivery.sentTime
                                ? getFormattedDate(delivery.sentTime)
                                : "-"}
                            </Table.Cell>
                          </Table.Row>
                        </Table.Body>
                      </Table.Root>
                    </Table.ScrollArea>

                    <Steps.Root
                      defaultStep={
                        deliverySteps.findIndex(
                          (step) => step.status == delivery.status,
                        ) + 1
                      }
                      count={deliverySteps.length}
                      mt={4}
                      mb={4}
                    >
                      <Steps.List>
                        {deliverySteps.map((step, index) => (
                          <Steps.Item key={index} index={index}>
                            <Steps.Indicator />
                            <Steps.Title>{step.title}</Steps.Title>
                            <Steps.Separator />
                          </Steps.Item>
                        ))}
                      </Steps.List>
                    </Steps.Root>
                    <Separator />
                  </Box>
                ))}
              </Card.Body>
            </Card.Root>
          </SimpleGrid>

          <Card.Root>
            <Card.Header>
              <Heading size="md">Fakturalinjer</Heading>
            </Card.Header>

            <Card.Body pt={0}>
              {isDraft ? (
                <EditableInvoiceLines invoice={invoice} />
              ) : (
                <ReadOnlyInvoiceLines invoice={invoice} />
              )}
            </Card.Body>
          </Card.Root>
        </>
      )}
    </Stack>
  );
}
